import React, { useState, useMemo } from "react";
import { Table, Button, Modal, Select, Input } from "antd";
import TemplateForm from './TemplateForm';
import {
  EditOutlined,
  EyeOutlined,
  StarOutlined,
  StarFilled,
  LockOutlined,
  LockFilled
} from "@ant-design/icons";
import PhysicianAPI from '../../axios/physician/PhysicianApi';
import { StyledFiltersSection, Label, StyledSpin } from './styled';
import { TemplateTypeEnum, EXTRA_MESSAGE_CASE_STATUS_NAMES } from '../../types/enums';

const { CASE, MESSAGE, VISIT } = TemplateTypeEnum

const TemplatesTable = ({
  type,
  user,
  templates,
  optimisticEdit,
  optimisticDelete,
  templateFilters,
  telemedicineCategories,
  caseStatuses,
  setTemplateFilters,
  handleFinish,
  searchTemplates,
  loading,
}) => {
  const [templateFormIsOpen, setTemplateFormIsOpen] = useState(false);
  const [templateFormMode, setTemplateFormMode] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const isVisitType = type === VISIT;

  const selectedTemplate = useMemo(() => {
    return templates.find(template => template._id === selectedTemplateId) || {};
  }, [templates, selectedTemplateId])

  const _handleFinish = () => {
    setTemplateFormIsOpen(false)
    handleFinish()
  }

  const openTemplateForm = (mode, template) => {
    setTemplateFormMode(mode);
    setTemplateFormIsOpen(true);
    setSelectedTemplateId(template._id);
  }

  const handleUpdate = async fn => {
    await fn()
    _handleFinish()
    searchTemplates({ type })
  }

  const onSuccess = () => {
    setTemplateFormIsOpen(false)
    handleFinish()
  }

  const handleOptimisticEdit = (templateId, values) =>
    optimisticEdit(templateId, values, { onSuccess })

  const handleOptimisticDelete = (templateId) =>
    optimisticDelete(templateId, { onSuccess })

  const caseStatusSelectOptionsByTemplateType = {
    [CASE]: caseStatuses,
    [MESSAGE]: caseStatuses.filter(({ name }) => EXTRA_MESSAGE_CASE_STATUS_NAMES.includes(name)),
  }

  const caseStatusSelectOptions = caseStatusSelectOptionsByTemplateType[type] || [];

  const canEdit = (template) => isVisitType || template.ownerId === user?._id;

  return (
    <StyledSpin spinning={loading}>
      <StyledFiltersSection>

        {!isVisitType && (
          <Label label='Owner:'>
            <Select id="template-table-owner-select" style={{ width: '220px'}} value={templateFilters.owner} onChange={(value) => setTemplateFilters(filters => ({ ...filters, owner: value }))} popupMatchSelectWidth={false}>
              <Select.Option value={null} label='All'>All</Select.Option>
              <Select.Option value='Admin'>Admin Templates</Select.Option>
              <Select.Option value={user?._id}>My Templates</Select.Option>
            </Select>
          </Label>
        )}

        <Label label='Telemedicine Category:'>
          <Select id="template-table-telemedicine-category-select" style={{ width: '220px'}} value={templateFilters.telemedicineCategory} onChange={(value) => setTemplateFilters(filters => ({ ...filters, telemedicineCategory: value }))} popupMatchSelectWidth={false}>
            <Select.Option value='All'>All</Select.Option>
            {telemedicineCategories.map(({ _id, name }) => <Select.Option key={_id} value={_id}>{name}</Select.Option>)}
          </Select>
        </Label>

        {!isVisitType && (
          <Label label='Case Status:'>
            <Select id="template-table-case-status-select" style={{ width: '220px'}} value={templateFilters.caseStatus} onChange={(value) => setTemplateFilters(filters => ({ ...filters, caseStatus: value }))} popupMatchSelectWidth={false}>
              <Select.Option value='All'>All</Select.Option>
              {caseStatusSelectOptions.map(({ _id, name }) => <Select.Option key={_id} value={_id}>{name}</Select.Option>)}
            </Select>
          </Label>
        )}

        <Label label='Search:'>
          <Input id="template-table-search-input" style={{ width: '220px'}} value={templateFilters.searchValue} onChange={(event) => {
            const { value } = event.target;
            setTemplateFilters(filters => ({ ...filters, searchValue: value }))
          }} />
        </Label>
      </StyledFiltersSection>
      <Table
        columns={[
          {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: '20%',
            onFilter: (filters, record) => {
              let ownerFilter;

              if (isVisitType) {
                ownerFilter = true;
              } else {
                switch (filters.owner) {
                  case 'Shared':
                    ownerFilter = !!record.ownerId && record.ownerId !== user?._id;
                    break;
                  case 'Admin':
                    ownerFilter = !record.ownerId;
                    break;
                  case user?._id:
                    ownerFilter = String(record.ownerId) === filters.owner;
                    break;
                  default:
                    ownerFilter = true;
                }
              }

              const telemedicineCategoryFilter = filters.telemedicineCategory && filters.telemedicineCategory !== 'All'
                  ? record.teleMedicineCategoryIds?.find(category => category._id === filters.telemedicineCategory)
                  : true

              const caseStatusFilter = !isVisitType && filters.caseStatus && filters.caseStatus !== 'All'
                  ? String(record.statusId?._id) === filters.caseStatus
                  : true

              const searchValueFilter = !filters.searchValue || (record.title?.toLowerCase().includes(filters.searchValue?.toLowerCase()) || record.message?.toLowerCase().includes(filters.searchValue?.toLowerCase()))

              return ownerFilter && telemedicineCategoryFilter && caseStatusFilter && searchValueFilter;
            },
            filteredValue: [templateFilters]
          },
          {
            title: "Telemedicine Category",
            dataIndex: "teleMedicineCategories",
            key: "telemedicine-category",
            width: '20%',
            render: (vals) => {
              return (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                maxHeight: '100px',
                overflow: 'scroll'
              }}>
                <ul>
                  {vals?.split(',').map(val => <li key={val}>{val}</li>)}
                </ul>
              </div>
            )}
          },
          ...(!isVisitType ? [{
            title: "Assigned Status",
            dataIndex: "caseStatus",
            key: "case-status",
            width: '20%'
          }] : []),
          {
            title: 'Actions',
            align: "right",
            render: (template) => (
              <>
                <Button
                  id={`template-table-${template._id}-view-btn`}
                  shape="circle"
                  icon={<EyeOutlined />}
                  onClick={() => openTemplateForm('View', template)}
                  style={{ marginRight: 15 }}
                />
                {canEdit(template) && (
                  <Button
                    id={`template-table-${template._id}-edit-btn`}
                    shape="circle"
                    icon={<EditOutlined />}
                    onClick={() => openTemplateForm('Edit', template)}
                    style={{ marginRight: 15 }}
                  />
                )}
                {!isVisitType && (
                  <>
                    <Button
                      id={`template-table-${template._id}-favorite-btn`}
                      shape='circle'
                      icon={template.isFavorite ? <StarFilled style={{ color: '#7BAD7E' }} /> : <StarOutlined style={{ color: '#7BAD7E' }} />}
                      onClick={() => handleUpdate(() => PhysicianAPI.toggleFavoriteTemplate(template._id))}
                    />
                    {template.ownerId === user?._id && (
                      <Button
                        id={`template-table-${template._id}-privacy-btn`}
                        shape='circle'
                        icon={template.isPrivate ? <LockFilled style={{ color: '#edac1f' }} /> : <LockOutlined style={{ color: '#edac1f' }} />}
                        onClick={() => handleUpdate(() => PhysicianAPI.togglePrivateTemplate(template._id))}
                        style={{ marginLeft: 15 }}
                      />
                    )}
                  </>
                )}
              </>
            ),
          }
        ]}
        dataSource={templates}
        pagination={false}
        scroll={{ y: '70vh', x: true }}
      />
      <Modal
        maskClosable
        centered
        closable={false}
        footer={null}
        open={templateFormIsOpen}
        width='50vw'
        onCancel={() => setTemplateFormIsOpen(false)}
        destroyOnClose
      >
        <TemplateForm
          mode={templateFormMode}
          type={type}
          template={selectedTemplate}
          isOwner={selectedTemplate.ownerId === user?._id}
          handleFinish={_handleFinish}
          searchTemplates={searchTemplates}
          onEdit={isVisitType ? handleOptimisticEdit : undefined}
          onDelete={isVisitType ? handleOptimisticDelete : undefined}
        />
      </Modal>
    </StyledSpin>
  );
}

export default TemplatesTable;
